import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { Field } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Select } from '@/components/ui/Select'
import type { OfferLanguage, SendQuotePayload } from '@/features/quotes/api/useQuote'
import { useFormat } from '@/i18n/useFormat'
import { recipientRefusal, recipientState } from '@/features/quotes/quoteReadiness'
import { nextRevisionLabel } from '@/features/quotes/quoteText'
import type { CurrentUser, QuoteDetail } from '@/types/domain'

/* Sending the commercial offer (FR-71, Story 2.6).
 *
 * A sheet rather than a bare button press, for the reason the case status send
 * is a sheet: an email leaves permanently, and the address it leaves for must
 * be on screen BEFORE anyone commits. The press that issues Rev A is the least
 * reversible action in the product — it snapshots an immutable revision, renders
 * a PDF and writes to a client — so the one thing this surface owes the user is
 * a last look at where it is going.
 *
 * It deliberately asks almost nothing. The recipient defaults to the client's
 * primary contact; the sheet exists to SHOW those defaults, not to make somebody
 * re-enter them. Sending is one more click from opening it.
 *
 * The LANGUAGE is not this sheet's state, and used to be. It held its own
 * `useState('ru')`, so a reader who had just checked the English preview met a
 * send form set to Russian and issued the document they had not read. It is the
 * page's now, shared with the preview sheet, which is the only arrangement in
 * which "what I checked is what goes out" is true — see QuoteDetailPage.
 */

const LANGUAGES = ['ru', 'en'] as const satisfies readonly OfferLanguage[]

export function OfferSendSheet({
  open,
  onClose,
  quote,
  user,
  onSubmit,
  pending,
  submitError,
  language,
  onLanguageChange,
}: {
  open: boolean
  onClose: () => void
  quote: QuoteDetail
  /** The reader, for the one capability this sheet asks about: whether they
   *  may read the client's contacts (`directory.contact.read`). */
  user: CurrentUser | undefined
  onSubmit: (values: SendQuotePayload) => void
  pending?: boolean
  submitError?: string
  /** The language the offer prints in — the page's, shared with the preview. */
  language: OfferLanguage
  onLanguageChange: (language: OfferLanguage) => void
}) {
  const { t } = useTranslation()
  const format = useFormat()

  /* The address the offer goes to, as the server would resolve it. Absent when
     the client has no contact carrying an email — which is a refusal rather
     than a blank field, because the fix is in the directory and not here.

     ABSENT for a second reason, and it must not be worded as the first (Q4,
     owner 2026-10-11). A role without `directory.contact.read` is served no
     `clientContactEmail` at all, and the server refuses its send with a 403:
     the address is somebody else's to see, so the send is somebody else's to
     make. Telling that reader "the client has no contact with an email" would
     send them to the directory to add one that already exists. The capability
     is asked first, so it decides even if an address did arrive — the server
     refuses that send regardless. `recipientState` holds that order, and the
     quote page's client block and readiness card read the same answer. */
  const recipientNow = recipientState(quote, user)
  const recipient = recipientNow.kind === 'ok' ? recipientNow.email : ''
  const blockedReason = recipientRefusal(recipientNow, t)

  return (
    <FormSheet
      open={open}
      /* The language is NOT reset on close. It belongs to the page and the
         preview reads the same value: resetting it here would silently undo a
         choice the reader made in the sheet they came from. */
      onClose={onClose}
      title={t('quotes.offerSend.title')}
      submitLabel={t('quotes.actions.sendOffer')}
      onSubmit={() =>
        onSubmit({
          // The recipient is deliberately NOT sent when it is the default one:
          // an empty list tells the server "the client's primary contact", so a
          // contact corrected between opening this sheet and pressing send is
          // honoured rather than overridden by a stale address.
          language,
        })
      }
      pending={pending}
      /* A refusal the reader can act on (ask a colleague, fix the directory):
         `blockedReason`, so the button keeps its tab stop and its tooltip, and
         the same sentence as the sheet's notice so a phone, which has no
         hover, reads it too. `pending` stays the only real `disabled`. */
      blockedReason={blockedReason}
      error={submitError}
      notice={blockedReason}
    >
      <div className="flex flex-col gap-4">
        {/* O7 (S-2a): the price was asked for and is not ready yet. A WARNING,
            never a gate — the server sends regardless, and a salesperson who
            agreed the figure with Pricing by phone must not be stopped by a
            button nobody pressed. It is said here, before the mail leaves,
            because this is the last look anybody takes. */}
        {quote.pricing.state === 'requested' ? (
          <Banner tone="attention" title={t('quotes.pricingState.sendWarning')}>
            {t('quotes.pricingState.sendWarningBody')}
          </Banner>
        ) : null}

        {/* What is about to be issued. The revision letter is the fact the user
            is least likely to be holding in their head, and it is what the
            client will quote back. */}
        <div className="border-line bg-surface-sunken flex flex-col gap-1.5 rounded-lg border px-4 py-3">
          <span className="asa-eyebrow">{t('quotes.offerSend.document')}</span>
          <span className="text-text-heading text-[13px] leading-snug">
            {quote.number}
            {quote.revisions && quote.revisions.length > 0 ? (
              <span className="text-text-muted">
                {' '}
                {t('quotes.offerSend.nextRevision', {
                  label: nextRevisionLabel(quote.revisions.length),
                })}
              </span>
            ) : null}
          </span>
          {quote.validUntil ? (
            <span className="text-text-muted text-[12px] leading-snug">
              {/* The sheet's own sentence, in the reader's language like the
                  rest of it — the offer's print language governs the PDF, not
                  this line. */}
              {t('quotes.offerSend.validUntil', { date: format.date(quote.validUntil) })}
            </span>
          ) : null}
        </div>

        {recipientNow.kind !== 'ok' ? null : (
          <div className="border-line bg-surface-sunken flex flex-col gap-1.5 rounded-lg border px-4 py-3">
            <span className="asa-eyebrow">{t('quotes.offerSend.recipient')}</span>
            <span className="text-text-heading text-[13px] leading-snug">
              {quote.clientContactName ? (
                <>
                  {quote.clientContactName}{' '}
                  <span className="text-text-muted">&lt;{recipient}&gt;</span>
                </>
              ) : (
                recipient
              )}
            </span>
          </div>
        )}

        <Field label={t('cases.statusSend.language')}>
          {(props) => (
            <Select
              {...props}
              value={language}
              onChange={(next) => onLanguageChange(next as OfferLanguage)}
              options={LANGUAGES.map((code) => ({
                value: code,
                label: t(code === 'ru' ? 'common.language.russian' : 'common.language.english'),
              }))}
            />
          )}
        </Field>

        <p className="text-text-muted text-[12px] leading-snug">
          {t('quotes.offerSend.explainer')}
        </p>
      </div>
    </FormSheet>
  )
}
